/**
 * helpHubApiService.ts
 *
 * API service interface for HelpHub.
 * Provides abstraction layer so HelpHub can work in both:
 * - Main app (calls /api/help directly)
 * - Widget (calls /api/management/help-hub via proxy)
 */
import helpHubService, {
  Ticket,
  TicketSubmission,
  ChatRequest,
  ChatResponse,
} from './helpHubService';

// Re-export types for convenience
export type {
  Ticket,
  TicketSubmission,
  ChatRequest,
  ChatResponse,
};

// ============================================
// TYPES
// ============================================

export interface SystemStatus {
  status: 'operational' | 'degraded' | 'down';
  uptime: string;
  lastChecked: string | Date;
}

export interface FrillToken {
  urls?: {
    ideas: string;
    roadmap: string;
    changelog: string;
  };
  token: string;
  expiresAt: string | Date;
}

export interface UserTicket {
  id: string;
  subject: string;
status: 'open' | 'in-progress' | 'resolved' | 'closed';
  createdAt: Date;
  updatedAt: Date;
}

export interface Workspace {
  id: number;
  name: string;
  subscription_status: string;
  subscription_tier?: string;
  base_license_features?: Record<string, boolean>;
}

export interface HelpHubApiService {
  // Data fetching
  getWorkspaces: () => Promise<Workspace[]>;
  getSystemStatus: () => Promise<SystemStatus>;
getFrillToken: () => Promise<FrillToken>;
  getBetterModeToken: () => Promise<{ token: string; expiresAt: Date }>;
  getCommunitySSOUrl: (token: string) => string;
  getUserTickets: () => Promise<UserTicket[]>;
  getUnreadChangelogCount: () => Promise<number>;

  // Actions
  sendChatMessage: (request: ChatRequest, isGuest?: boolean) => Promise<ChatResponse>;
  submitTicket: (ticket: TicketSubmission) => Promise<{ ticketId: string; success: boolean }>;

  // HelpCrunch (optional - may not be available in widget)
  loadHelpCrunchSDK?: () => Promise<void>;
  openHelpCrunch?: (user: any) => void;
  addHelpCrunchCloseBar?: (callback: () => void) => void;
  closeHelpCrunchAndReturn?: () => void;

  // Consent
  logConsent?: () => Promise<void>;
}

// ============================================
// DEFAULT IMPLEMENTATION
// Wraps helpHubService for main app usage
// ============================================

export function createDefaultApiService(): HelpHubApiService {
  return {
    // Data fetching - these need to be implemented by the caller
    // since they depend on auth context
    getWorkspaces: async () => {
      try {
        const response = await fetch('/api/workspaces', {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`
          }
        });
        if (!response.ok) return [];
        const data = await response.json();
        return data.workspaces || data || [];
      } catch {
        return [];
      }
    },
    getSystemStatus: () => helpHubService.getSystemStatus(),
getFrillToken: () => helpHubService.getFrillToken(),
    getBetterModeToken: () => helpHubService.getBetterModeToken(),
    getCommunitySSOUrl: (token: string) => helpHubService.getCommunitySSOUrl(token),
    getUserTickets: () => helpHubService.getUserTickets(),
    getUnreadChangelogCount: () => helpHubService.getUnreadChangelogCount(),

    // Actions
    sendChatMessage: (request, isGuest) => helpHubService.sendChatMessage(request, isGuest),
    submitTicket: (ticket) => helpHubService.submitTicket(ticket),

    // HelpCrunch
    loadHelpCrunchSDK: () => helpHubService.loadHelpCrunchSDK(),
    openHelpCrunch: (user) => helpHubService.openHelpCrunch(user),
    addHelpCrunchCloseBar: (callback) => helpHubService.addHelpCrunchCloseBar(callback),
    closeHelpCrunchAndReturn: () => helpHubService.closeHelpCrunchAndReturn(),

    // Consent
    logConsent: async () => {
      try {
        await fetch('/api/help/consent/log', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`
          }
        });
      } catch (e) { console.warn('[HelpHub] Failed to log consent:', e); }
    },
  };
}

export default createDefaultApiService;
